/**
 * TradingView Datafeed for TopStep / ProjectX futures charts
 * Uses the /api/topstep/bars endpoint to serve OHLCV data.
 */

import api from '../services/api';

interface Bar {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

interface ContractInfo {
  contractId: string;
  name: string;
  description: string;
  tickSize: number;
  tickValue?: number;
  symbolId?: string;
}

// Map TV symbol → contract info
const contractMap: Record<string, ContractInfo> = {};

const PROJECTX_SYMBOL_ALIASES: Record<string, string> = {
  TUA: 'ZT',
  TYA: 'ZN',
  USA: 'ZB',
};

export function normalizeTopStepSymbolKey(value?: string): string {
  const raw = String(value || '')
    .trim()
    .toUpperCase()
    .replace(/^TOPSTEP:/, '')
    .replace(/^F\.US\./, '')
    .replace(/^CON\.F\.US\./, '')
    .replace(/\..*$/, '');

  const root = raw.replace(/[FGHJKMNQUVXZ]\d+$/i, '');
  return PROJECTX_SYMBOL_ALIASES[root] || PROJECTX_SYMBOL_ALIASES[raw] || root || raw;
}

export function registerContract(symbol: string, info: ContractInfo) {
  const raw = String(symbol || '').trim().toUpperCase();
  if (!raw) return;

  const aliases = new Set<string>([
    raw.replace(/^TOPSTEP:/, ''),
    normalizeTopStepSymbolKey(raw),
    normalizeTopStepSymbolKey(info.name),
    normalizeTopStepSymbolKey(info.symbolId),
    normalizeTopStepSymbolKey(info.contractId),
  ].filter(Boolean));

  for (const alias of aliases) {
    contractMap[alias] = info;
  }
}

// unit codes: 1=Minute 2=Hour 3=Day 4=Week
const resolutionMap: Record<string, { unit: number; unitNumber: number }> = {
  '1':   { unit: 1, unitNumber: 1 },
  '5':   { unit: 1, unitNumber: 5 },
  '15':  { unit: 1, unitNumber: 15 },
  '30':  { unit: 1, unitNumber: 30 },
  '60':  { unit: 2, unitNumber: 1 },
  '240': { unit: 2, unitNumber: 4 },
  'D':   { unit: 3, unitNumber: 1 },
  'W':   { unit: 4, unitNumber: 1 },
};

export function createTopStepDatafeed(apiBasePath = '/topstep') {
  const subscribers = new Map<string, ReturnType<typeof setInterval>>();

  return {
    onReady: (callback: (config: unknown) => void) => {
      setTimeout(() => {
        callback({
          supported_resolutions: ['1', '5', '15', '30', '60', '240', 'D', 'W'],
          exchanges: [{ value: 'TOPSTEP', name: 'TopStep/ProjectX', desc: 'CME Micro Futures' }],
          symbols_types: [{ name: 'futures', value: 'futures' }],
        });
      }, 0);
    },

    searchSymbols: (
      _userInput: string,
      _exchange: string,
      _symbolType: string,
      onResult: (result: unknown[]) => void,
    ) => {
      onResult(
        Object.entries(contractMap).map(([sym, info]) => ({
          symbol: sym,
          full_name: `TOPSTEP:${sym}`,
          description: info.description,
          exchange: 'TOPSTEP',
          type: 'futures',
        })),
      );
    },

    resolveSymbol: (
      symbolName: string,
      onResolved: (info: unknown) => void,
      onError: (reason: string) => void,
    ) => {
      const requested = symbolName.replace('TOPSTEP:', '').toUpperCase();
      const key = contractMap[requested] ? requested : normalizeTopStepSymbolKey(requested);
      const info = contractMap[key];
      if (!info) {
        setTimeout(() => onError(`Unknown symbol: ${symbolName}`), 0);
        return;
      }
      // TradingView requires resolveSymbol to call back asynchronously
      setTimeout(() => onResolved({
        name: key,
        full_name: `TOPSTEP:${key}`,
        description: info.description,
        type: 'futures',
        session: '0000-2359',
        timezone: 'America/New_York',
        exchange: 'TOPSTEP',
        listed_exchange: 'CME',
        format: 'price',
        minmov: 1,
        pricescale: Math.round(1 / info.tickSize),
        has_intraday: true,
        has_daily: true,
        intraday_multipliers: ['1', '5', '15', '30', '60', '240'],
        supported_resolutions: ['1', '5', '15', '30', '60', '240', 'D', 'W'],
        volume_precision: 0,
        data_status: 'streaming',
      }), 0);
    },

    getBars: async (
      symbolInfo: any,
      resolution: string,
      periodParams: { from: number; to: number; countBack?: number },
      onResult: (bars: Bar[], meta: { noData: boolean }) => void,
      onError: (reason: string) => void,
    ) => {
      const key = symbolInfo.name?.toUpperCase();
      const info = contractMap[key];
      if (!info) { onError('No contract info'); return; }

      const resConfig = resolutionMap[resolution] || { unit: 3, unitNumber: 1 };
      const limit = periodParams.countBack || 200;

      try {
        const qs = new URLSearchParams({
          contractId: info.contractId,
          unit: String(resConfig.unit),
          unitNumber: String(resConfig.unitNumber),
          limit: String(Math.min(limit, 500)),
        });
        const res = await api.get(`${apiBasePath}/bars?${qs}`);
        const rawBars: any[] = res.data?.data || [];

        if (!rawBars.length) {
          onResult([], { noData: true });
          return;
        }

        const bars: Bar[] = rawBars
          .map(b => ({
            time: new Date(b.timestamp).getTime(),
            open: b.open,
            high: b.high,
            low: b.low,
            close: b.close,
            volume: b.volume,
          }))
          .filter(b => b.time >= periodParams.from * 1000 && b.time <= periodParams.to * 1000)
          .sort((a, b) => a.time - b.time);

        onResult(bars, { noData: bars.length === 0 });
      } catch (err: any) {
        onError(err.message || 'getBars failed');
      }
    },

    subscribeBars: (
      symbolInfo: any,
      resolution: string,
      onTick: (bar: Bar) => void,
      subscriberUID: string,
    ) => {
      // Poll every 30s for "live" updates on the current bar
      const key = symbolInfo.name?.toUpperCase();
      const info = contractMap[key];
      if (!info) return;

      const resConfig = resolutionMap[resolution] || { unit: 3, unitNumber: 1 };

      const intervalId = setInterval(async () => {
        try {
          const qs = new URLSearchParams({
            contractId: info.contractId,
            unit: String(resConfig.unit),
            unitNumber: String(resConfig.unitNumber),
            limit: '2',
          });
          const res = await api.get(`${apiBasePath}/bars?${qs}`);
          const rawBars: any[] = res.data?.data || [];
          if (rawBars.length > 0) {
            const last = rawBars[rawBars.length - 1];
            onTick({
              time: new Date(last.timestamp).getTime(),
              open: last.open,
              high: last.high,
              low: last.low,
              close: last.close,
              volume: last.volume,
            });
          }
        } catch { /* noop */ }
      }, 30000);

      subscribers.set(subscriberUID, intervalId);
    },

    unsubscribeBars: (subscriberUID: string) => {
      const id = subscribers.get(subscriberUID);
      if (id) { clearInterval(id); subscribers.delete(subscriberUID); }
    },
  };
}
